import { Component, For, Show } from "solid-js"
import { useScenarioStore } from "../stores/scenario"
import { formatSimTime } from "../types"

const MAX_CONTENT_LENGTH = 600

const AnalysisPanel: Component = () => {
    const [scenario, scenarioActions] = useScenarioStore();
    
    const handleAdd = () => {
        scenarioActions.addAnalysisSection()
        scenarioActions.scheduleSave()
    }

    const handleTimeChange = (id: string, field: "hour" | "minute" | "second", value: string) => {
        const section = scenario.analysis.sections.find((s) => s.id === id)
        if (!section) return

        // Allow empty string (user is typing)
        if (value === "") {
            scenarioActions.updateAnalysisSection(id, { time: { ...section.time, [field]: 0 } })
            return
        }

        const num = parseInt(value, 10)
        if (isNaN(num)) return
        scenarioActions.updateAnalysisSection(id, { time: { ...section.time, [field]: num } })
    }

    const handleContentChange = (id: string, value: string) => {
        // Limit to max length
        const limited = value.slice(0, MAX_CONTENT_LENGTH)
        scenarioActions.updateAnalysisSection(id, { content: limited })
    }

    const handleMove = (id: string, direction: "up" | "down") => {
        scenarioActions.moveAnalysisSection(id, direction)
        scenarioActions.scheduleSave()
    }

    return (
        <>
            <div class="border-b border-gray-200 bg-white p-3">
                <h2 class="mb-2 text-sm font-semibold text-gray-700">Analys</h2>
                <p class="mb-2 text-[10px] text-gray-400">
                    Mölighet att lägga till ett analysavsnitt som visas i analysmodulen. Avsnitt sorteras efter
                    tidsstämpel och kan ordnas manuellt.
                </p>

                {/* Ingress field */}
                <div class="mb-2">
                    <label class="mb-1 block text-xs text-gray-600">Ingress (valfri introduktion):</label>
                    <textarea
                        value={scenario.analysis.ingress || ""}
                        onInput={(e) => scenarioActions.setAnalysisIngress(e.currentTarget.value)}
                        onBlur={() => scenarioActions.scheduleSave()}
                        placeholder="Skriv en introduktion till analysen..."
                        class="w-full resize-none rounded border border-gray-300 px-2 py-1.5 text-xs"
                        rows={3}
                    />
                </div>

                <button
                    class="w-full rounded bg-blue-500 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-600"
                    onClick={handleAdd}
                >
                    + Lägg till analysavsnitt
                </button>
            </div>

            <div class="flex-1 overflow-y-auto">
                <For each={scenario.analysis.sections}>
                    {(section, index) => (
                        <div class="space-y-1.5 border-b border-gray-200 px-3 py-2">
                            <div class="flex items-center justify-between">
                                <span class="font-mono text-xs text-gray-500">
                                    {formatSimTime(section.time)} (#{section.order + 1})
                                </span>
                                <div class="flex items-center gap-2">
                                    {/* Move buttons */}
                                    <button
                                        class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                        onClick={() => handleMove(section.id, "up")}
                                        disabled={index() === 0}
                                        title="Flytta upp"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        class="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                        onClick={() => handleMove(section.id, "down")}
                                        disabled={index() === scenario.analysis.sections.length - 1}
                                        title="Flytta ner"
                                    >
                                        ↓
                                    </button>
                                    {/* Remove button */}
                                    <button
                                        class="text-xs text-red-400 hover:text-red-600"
                                        onClick={() => {
                                            scenarioActions.removeAnalysisSection(section.id)
                                            scenarioActions.scheduleSave()
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            {/* Time inputs */}
                            <div class="flex gap-1">
                                <input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={section.time.hour}
                                    onInput={(e) => handleTimeChange(section.id, "hour", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                                <span class="self-center text-xs text-gray-400">:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={section.time.minute}
                                    onInput={(e) => handleTimeChange(section.id, "minute", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                                <span class="self-center text-xs text-gray-400">:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={section.time.second}
                                    onInput={(e) => handleTimeChange(section.id, "second", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                            </div>

                            {/* Headline input */}
                            <div>
                                <input
                                    type="text"
                                    value={section.headline || ""}
                                    onInput={(e) =>
                                        scenarioActions.updateAnalysisSection(section.id, {
                                            headline: e.currentTarget.value,
                                        })
                                    }
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    placeholder="Rubrik för avsnittet..."
                                    class="w-full rounded border border-gray-300 px-2 py-1 text-xs font-semibold"
                                />
                            </div>

                            {/* Content textarea */}
                            <div>
                                <textarea
                                    value={section.content}
                                    onInput={(e) => handleContentChange(section.id, e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    placeholder="Skriv analysinnehåll (max 600 tecken)..."
                                    class="w-full resize-none rounded border border-gray-300 px-2 py-1.5 text-xs"
                                    rows={4}
                                />
                                <div class="text-right text-[10px] text-gray-400">
                                    {section.content.length}/{MAX_CONTENT_LENGTH}
                                </div>
                            </div>
                        </div>
                    )}
                </For>

                <Show when={scenario.analysis.sections.length === 0}>
                    <div class="p-4 text-center text-sm text-gray-400">
                        Inga analysavsnitt ännu. Lägg till avsnitt ovan.
                    </div>
                </Show>
            </div>
        </>
    )
}

export default AnalysisPanel
